import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { AuthProvider } from "react-oidc-context";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "./store";
import { initI18n } from "../../../packages/i18n/src/config";
import App from "./App";
import "../../../index.css";
import { logout } from "./store/auth";
import {
  authenticateUser,
  handleAuthenticationError,
} from "@surgia/api/src";
import { Toaster } from "sonner";
import { getKeycloakUser, getKeycloakTokens } from "./utils/keyclock";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const i18n = initI18n();

const oidcConfig = {
  authority: import.meta.env.VITE_KEYCLOAK_ISSUER,
  client_id: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
  redirect_uri: window.location.origin,
  post_logout_redirect_uri: window.location.origin,
  silent_redirect_uri:
    import.meta.env.VITE_SILENT_SSO_URL ||
    `${window.location.origin}/silent-sso.html`,
  includeIdTokenInSilentRenew: true,
  response_type: "code",
  scope: "openid profile email",
  loadUserInfo: false,
  monitorSession: true,
  checkSessionInterval: 2000,
  revokeTokensOnSignout: true,
  automaticSilentRenew: false,

  onSigninCallback: async () => {
    window.history.replaceState({}, document.title, window.location.pathname);
    const keycloakUser = getKeycloakUser();
    if (keycloakUser?.access_token)
      localStorage.setItem("token", keycloakUser?.access_token || "");

    const keycloakTokens = getKeycloakTokens();

    try {
      const authResult = await authenticateUser(keycloakTokens);
      if (authResult.errorMessageKey == "Access denied") {
        store.dispatch(logout());
        localStorage.removeItem("token");
        sessionStorage.clear();
        window.location.href = `/wrong-portal`;
        return;
      } else if (!authResult?.hasAccess) {
        handleAuthenticationError(store, logout);
        const keycloakLogoutUrl = `${import.meta.env.VITE_KEYCLOAK_ISSUER}/protocol/openid-connect/logout?client_id=${import.meta.env.VITE_KEYCLOAK_CLIENT_ID}&post_logout_redirect_uri=${window.location.origin}`;
        window.location.href = keycloakLogoutUrl;
        return;
      }

      const tenant_id = authResult?.userInfo?.user_profile?.tenant?.id;

      const authDataPayload = {
        ...keycloakTokens,
        userInfo: {
          email: authResult.userInfo?.user_info?.email,
          first_name: authResult.userInfo?.user_info?.first_name,
          last_name: authResult.userInfo?.user_info?.last_name,
          full_name: authResult.userInfo?.user_info?.full_name,
          mobile: authResult.userInfo?.user_info?.mobile,
          subject_id: authResult.userInfo?.user_info?.subject_id,
          creation_date: authResult.userInfo?.user_info?.creation_date,
          last_accessed_branch_id: authResult.userInfo?.user_info?.last_accessed_branch,
          user_groups: authResult.userInfo?.user_info?.user_groups,
          last_login: authResult.userInfo?.user_info?.last_login,
          user_permissions: authResult.userInfo?.user_permissions,
          user_branches: authResult.userInfo?.user_info?.user_branches,
        },
        isAuthenticated: true,
        tenant_id: tenant_id,
        storefront_id: null,
        currency_code: null,
        exchange_rate: null,
      };

      sessionStorage.setItem(
        "pendingAuthData",
        JSON.stringify(authDataPayload)
      );

      const storedPath = sessionStorage.getItem("redirectPath");
      if (storedPath) {
        sessionStorage.removeItem("redirectPath");
        window.location.href = storedPath;
      } else {
        window.location.href = "/";
      }
    } catch (error: any) {
      console.error("Authentication error:", error);
    }
  },

  onSignoutCallback: () => {
    store.dispatch(logout());
    localStorage.clear();
    sessionStorage.clear();
  },
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider {...oidcConfig}>
              <App />
              <Toaster position="top-right" richColors={true} duration={1500} />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </I18nextProvider>
    </PersistGate>
  </Provider>
);




